<template>
  <f7-popup id="popup-proofread" class="popup-proofread">
    <f7-view>
      <f7-page :page-content="false">
        <f7-page-content>
          <f7-toolbar tabbar top no-shadow no-hairline>
            <f7-link tab-link="#tab-new" tab-link-active>New tasks</f7-link>
            <f7-link tab-link="#tab-history">My tasks</f7-link>
          </f7-toolbar>

          <f7-tabs>
            <f7-tab id="tab-new" tab-active @tab:show="tabShow('proofreads')">
              <f7-block v-if="loading" class="page-message">
                <f7-preloader></f7-preloader>
              </f7-block>

              <f7-block v-else-if="!loading && !proofreads.length" class="page-message">
                No tasks
              </f7-block>

              <f7-block v-else-if="!loading">
                <TaskCard
                  v-for="proofread in proofreads"
                  :key="proofread.id"
                  :data="proofread"
                  :active="proofread.id === currentTaskId"
                  :language-in="languagesIn.find(lang => lang.id === proofread.langIdIn)"
                  :language-out="
                    proofread.langIdOut
                      ? languagesOut.find(lang => lang.id === proofread.langIdOut)
                      : languagesIn.find(lang => lang.id === proofread.langIdIn)
                  "
                  :take-loading="takeLoading && takeLoadingId === proofread.id"
                  @view="viewTask"
                  @take="takeTask"
                ></TaskCard>
              </f7-block>
            </f7-tab>

            <f7-tab id="tab-history" @tab:show="tabShow('history')">
              <f7-block v-if="loading" class="page-message">
                <f7-preloader></f7-preloader>
              </f7-block>

              <f7-block v-else-if="!loading && !history.length" class="page-message">
                No tasks
              </f7-block>

              <f7-block v-else-if="!loading">
                <TaskCard
                  v-for="item in history"
                  :key="item.id"
                  :data="item"
                  :with-action="false"
                  :with-status="true"
                  :active="item.id === currentTaskId"
                  :language-in="languagesIn.find(lang => lang.id === item.langIdIn)"
                  :language-out="
                    item.langIdOut
                      ? languagesOut.find(lang => lang.id === item.langIdOut)
                      : languagesIn.find(lang => lang.id === item.langIdIn)
                  "
                  @view="viewTask"
                  @take="takeTask"
                ></TaskCard>
              </f7-block>
            </f7-tab>
          </f7-tabs>
        </f7-page-content>

        <f7-page-content v-if="!currentTaskId">
          <span class="page-message">
            Select task
          </span>
        </f7-page-content>

        <f7-page-content v-else>
          <f7-block-title>
            Source
          </f7-block-title>
          <f7-list class="source-input" no-hairlines-between no-hairlines inset>
            <f7-list-input type="textarea" readonly :value="textIn"></f7-list-input>
          </f7-list>

          <f7-block-title>
            Result
          </f7-block-title>
          <f7-block>
            <wysiwyg v-model="textOut"></wysiwyg>
          </f7-block>

          <f7-block
            v-if="currentTabName === 'history'"
            class="display-flex justify-content-flex-end"
          >
            <f7-button
              class="send-out-button gradient"
              round
              large
              fill
              color="pink"
              @click="saveTask"
            >
              Save
            </f7-button>
          </f7-block>
        </f7-page-content>
      </f7-page>
    </f7-view>
  </f7-popup>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import apiRequest from '../js/helpers/apiRequest.js';
import notify from '../js/helpers/notify.js';

import TaskCard from '../components/TaskCard.vue';

export default {
  name: 'PopupProofread',

  components: {
    TaskCard
  },

  data() {
    return {
      loading: true,
      takeLoading: false,
      takeLoadingId: null,
      textIn: '',
      textOut: '',
      currentTabName: 'proofreads'
    };
  },

  computed: {
    ...mapState({
      languagesIn: state => state.proofread.languages.in,
      languagesOut: state => state.proofread.languages.out,
      proofreads: state => state.proofread.proofreads,
      history: state => state.proofread.history,
      currentTaskId: state => state.proofread.currentTaskId
    })
  },

  async created() {
    await this.getTranslatedLanguages();
    await this.getProofreads();
    this.getProofreadsHistory();
    this.loading = false;
  },

  methods: {
    tabShow(name) {
      this.currentTabName = name;
      this.$store.commit('proofread/SET_CURRENT_TASK', null);
    },

    viewTask(id) {
      this.$store.commit('proofread/SET_CURRENT_TASK', id);
      const proofread = this[this.currentTabName].find(proofread => proofread.id === id);
      this.textIn = proofread.textIn;
      this.textOut = proofread.textOut ? proofread.textOut : proofread.textIn;
    },

    async saveTask() {
      try {
        const body = new FormData();
        body.append('text_out', this.textOut);
        const res = await apiRequest(
          `teacher/proofread/save/${this.currentTaskId}`,
          'POST',
          body,
          true
        );

        if (res.error) {
          notify({ text: res.error });
        } else {
          notify({ text: 'Task has been completed!' });
          await this.getProofreadsHistory();
        }
      } catch (error) {
        console.log(error);
      }
    },

    async takeTask(id) {
      try {
        this.takeLoading = true;
        this.takeLoadingId = id;
        await apiRequest(`teacher/proofread/${id}`, 'POST', null, true);
        await this.getProofreads();
        await this.getProofreadsHistory();
        this.takeLoading = false;
        this.takeLoadingId = null;
        this.$f7.tab.show('#tab-history');
        this.currentTabName = 'history';
        this.$store.commit('proofread/SET_CURRENT_TASK', id);
      } catch (error) {
        console.log(error);
      }
    },

    ...mapActions({
      getTranslatedLanguages: 'proofread/getTranslatedLanguages',
      getProofreads: 'proofread/getProofreads',
      getProofreadsHistory: 'proofread/getProofreadsHistory'
    })
  }
};
</script>

<style lang="less">
#popup-proofread.popup:not(.popup-tablet-fullscreen) {
  @media (min-height: 630px) and (min-width: 630px) {
    --f7-popup-tablet-width: 970px;
    --f7-popup-tablet-height: 526px;
  }
}

.popup-proofread {
  .tab-link {
    transition: all 0.25s ease;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background-color: #d8d8d8;
    }
  }

  .tab-link-active {
    &:hover {
      &::after {
        background-color: #ff6189;
      }
    }

    &::after {
      background-color: #ff6189;
    }
  }

  .page {
    display: flex;
  }

  .page-content {
    flex-basis: 100%;

    &:first-of-type {
      flex-basis: 380px;
      flex-shrink: 0;
      border-right: 1px solid #d8d8d8;
    }
  }

  .editr {
  }

  .editr--content {
    max-height: 150px;
  }

  .source-input {
    --f7-textarea-height: 135px;
    --f7-list-item-padding-vertical: 15px;

    li {
      border: 1px solid #ff6189;
      border-radius: 5px;
    }
  }

  .send-out-button {
    --f7-button-padding-horizontal: 25px;
  }
}
</style>
